import { useCallback, useMemo, useState, type SubmitEvent } from "react";
import { catchError, of, tap } from "rxjs";

import { APILibraryCategory } from "@/core/data_sources/models/APILibraryCategory";
import { Button, TextInput } from "@/core/ui/atoms";
import { useStore } from "@/core/utils/hooks";
import { ErrorResponse } from "@/core/utils/request";

import { LibraryCategoriesStore } from "../data_sources/store";

type CreateCategoryDialogProps = {
  readonly onCancel: () => void;
  readonly onCreatedSuccess: () => void;
};

export function CreateCategoryDialog({
  onCancel,
  onCreatedSuccess: onCreated,
}: CreateCategoryDialogProps) {
  const { actions, isConnected } = useStore(LibraryCategoriesStore.instance);
  const [error, setError] = useState("");
  const [category, setCategory] = useState(
    new APILibraryCategory({ name: "" }),
  );
  const [saving, setSaving] = useState(false);
  const canSubmit = useMemo(
    () => category.isDataValid() && !saving && isConnected,
    [category, saving, isConnected],
  );

  const handleSubmit = useCallback(
    (event: SubmitEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!canSubmit) {
        setError("Name is required.");
        return;
      }
      setSaving(true);
      setError("");
      actions
        .createCategory(category.toRequestModel())
        .pipe(
          tap(() => {
            onCreated();
          }),
          catchError((requestError) => {
            setError(
              requestError instanceof ErrorResponse && requestError.status
                ? requestError.error.message
                : "Unable to create category.",
            );
            return of(null);
          }),
          tap(() => setSaving(false)),
        )
        .subscribe();
    },
    [actions, canSubmit, category, onCreated],
  );

  return (
    <form
      className="flex max-h-[calc(100vh-7.5rem)] w-md max-w-full flex-col overflow-hidden"
      onSubmit={handleSubmit}
    >
      <div className="shrink-0 border-b border-border px-6 pb-3 pt-5">
        <h2 className="text-[17px] font-semibold text-ink">Create category</h2>
        <p className="mt-1 text-[13px] text-ink-subtle">
          Categories organize templates within Library. All tenants will see the
          new category.
        </p>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
        <label className="block">
          <div className="mb-1 flex flex-row gap-1 items-center">
            <span className="block text-xs font-medium text-ink-muted">
              Name
            </span>
            <span className="text-xs font-medium text-red-500 pb-2">*</span>
          </div>
          <TextInput
            autoFocus
            onChange={(event) =>
              setCategory(
                (prev) =>
                  new APILibraryCategory({
                    ...prev.data,
                    name: event.target.value,
                  }),
              )
            }
            placeholder="Maintenance"
            value={category.data.name}
          />
        </label>
        {error ? <p className="text-xs text-critical">{error}</p> : null}
      </div>

      <div className="flex shrink-0 justify-end gap-2 border-t border-border bg-panel-muted px-6 py-3.5">
        <Button disabled={saving} onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={!canSubmit} type="submit" variant="primary">
          {saving ? "Creating..." : "Create category"}
        </Button>
      </div>
    </form>
  );
}
